import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useParams } from 'react-router-dom';
import { api } from '../api/client';
import { PageHeader } from '../components/Layout';
import {
  Badge,
  Card,
  JsonBlock,
  KeyValue,
  Pagination,
  QueryBoundary,
  Stat,
  Table,
  Td,
  Th,
} from '../components/ui';
import { dateTime, money, num, pct, signedMoney, titleCase } from '../lib/format';

const LIMIT = 100;

function metric(m: Record<string, unknown>, key: string): number | null {
  const v = m[key];
  const n = typeof v === 'string' ? Number(v) : typeof v === 'number' ? v : NaN;
  return Number.isFinite(n) ? n : null;
}

export default function BacktestDetail() {
  const { id } = useParams<{ id: string }>();
  const bid = Number(id);
  const [offset, setOffset] = useState(0);

  const q = useQuery({
    queryKey: ['backtest', bid],
    queryFn: () => api.backtest(bid),
    enabled: Number.isFinite(bid),
  });
  const trades = useQuery({
    queryKey: ['backtest-trades', bid, offset],
    queryFn: () => api.backtestTrades(bid, LIMIT, offset),
    enabled: Number.isFinite(bid),
  });

  const b = q.data;
  const m = b?.metrics ?? {};

  return (
    <>
      <PageHeader
        title={`Backtest #${id}`}
        description={b ? `${b.strategy_id} ${b.strategy_version} · ${b.symbol} ${b.timeframe}` : undefined}
        actions={
          <Link className="text-sm text-sky-400 hover:underline" to="/backtests">
            ← all backtests
          </Link>
        }
      />

      <QueryBoundary q={q}>
        {b && (
          <div className="space-y-5">
            {b.flags.length > 0 && (
              <Card className="border-amber-800/60 bg-amber-950/30 px-4 py-3 text-sm text-amber-200">
                <ul className="list-disc space-y-1 pl-5">
                  {b.flags.map((f) => (
                    <li key={f}>{titleCase(f)}</li>
                  ))}
                </ul>
              </Card>
            )}

            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              <Stat
                label="Profit factor"
                value={num(metric(m, 'profit_factor'), 2)}
                tone={(metric(m, 'profit_factor') ?? 0) >= 1.1 ? 'good' : 'bad'}
              />
              <Stat label="Trades" value={b.trade_count} />
              <Stat label="Win rate" value={pct(metric(m, 'win_rate'), 1)} />
              <Stat
                label="Max drawdown"
                value={pct(metric(m, 'max_drawdown'), 1)}
                tone={(metric(m, 'max_drawdown') ?? 0) > 0.2 ? 'bad' : undefined}
              />
            </div>

            <Card className="p-4">
              <KeyValue
                rows={[
                  ['Range', `${dateTime(b.from_utc)} → ${dateTime(b.to_utc)}`],
                  ['Initial balance', money(b.initial_balance)],
                  ['Final balance', money(b.final_balance)],
                  ['Ambiguous share', pct(b.ambiguous_share, 2)],
                  ['Seed', b.seed],
                  ['Config hash', b.config_hash],
                  ['Dataset hash', b.dataset_hash],
                  ['Run at', dateTime(b.created_at)],
                ]}
              />
            </Card>

            <Card className="p-4">
              <h2 className="mb-3 text-sm font-medium text-slate-300">All metrics</h2>
              <JsonBlock value={b.metrics} />
            </Card>

            <Card className="p-4">
              <h2 className="mb-3 text-sm font-medium text-slate-300">
                Breakdowns (by symbol / regime / month)
              </h2>
              <JsonBlock value={b.breakdowns} />
            </Card>

            <div>
              <h2 className="mb-2 text-sm font-medium text-slate-300">Trades</h2>
              <QueryBoundary q={trades} isEmpty={trades.data?.items.length === 0}>
                {trades.data && (
                  <>
                    <Table>
                      <thead>
                        <tr>
                          <Th>#</Th>
                          <Th>Side</Th>
                          <Th>Entry</Th>
                          <Th>Exit</Th>
                          <Th className="text-right">Price in → out</Th>
                          <Th className="text-right">R</Th>
                          <Th className="text-right">Net</Th>
                          <Th>Exit reason</Th>
                          <Th>Regime</Th>
                        </tr>
                      </thead>
                      <tbody>
                        {trades.data.items.map((t) => (
                          <tr key={t.trade_index} className="hover:bg-slate-800/30">
                            <Td className="tabular-nums">{t.trade_index}</Td>
                            <Td>
                              <Badge tone={t.direction === 'long' ? 'good' : 'bad'}>{t.direction}</Badge>
                            </Td>
                            <Td className="whitespace-nowrap text-xs text-slate-400">
                              {dateTime(t.entry_bar_utc)}
                            </Td>
                            <Td className="whitespace-nowrap text-xs text-slate-400">
                              {dateTime(t.exit_bar_utc)} <span className="text-slate-600">({t.bars_held} bars)</span>
                            </Td>
                            <Td className="text-right tabular-nums text-xs">
                              {num(t.entry_price, 5)} → {num(t.exit_price, 5)}
                            </Td>
                            <Td
                              className={`text-right tabular-nums ${t.r_multiple < 0 ? 'text-red-400' : 'text-emerald-400'}`}
                            >
                              {num(t.r_multiple, 2)}R
                            </Td>
                            <Td
                              className={`text-right tabular-nums ${Number(t.net_profit) < 0 ? 'text-red-400' : 'text-emerald-400'}`}
                            >
                              {signedMoney(t.net_profit)}
                            </Td>
                            <Td>
                              {t.ambiguous ? (
                                <Badge tone="warn">{titleCase(t.exit_reason)}</Badge>
                              ) : (
                                <span className="text-xs text-slate-400">{titleCase(t.exit_reason)}</span>
                              )}
                            </Td>
                            <Td className="text-xs text-slate-500">{t.entry_regime ?? '—'}</Td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                    <Pagination
                      total={trades.data.total}
                      limit={LIMIT}
                      offset={offset}
                      onChange={setOffset}
                    />
                  </>
                )}
              </QueryBoundary>
            </div>
          </div>
        )}
      </QueryBoundary>
    </>
  );
}
